import { TRASH_RETENTION_DAYS } from '../lib/labels.js'
import { ModalOverlay } from './ModalOverlay.js'

export type TrashTarget =
  | { kind: 'file'; id: string; name: string }
  | { kind: 'project'; id: string; name: string }

type Props = {
  target: TrashTarget | null
  onClose: () => void
  onConfirm: () => void
  isPending?: boolean
}

/**
 * ゴミ箱へ移す前の確認モーダル（issue #86）。
 * 1 クリック即実行を防ぎ、30 日自動削除・復元可・共有継続を明示する。
 */
export function MoveToTrashModal({ target, onClose, onConfirm, isPending = false }: Props) {
  if (!target) return null

  const title =
    target.kind === 'file' ? 'ファイルをゴミ箱に移しますか？' : 'プロジェクトをゴミ箱に移しますか？'

  return (
    <ModalOverlay
      className="bg-black/50"
      onBackdropClick={onClose}
      onEscape={onClose}
      aria-label="ゴミ箱への移動確認"
    >
      <div
        className="bg-card border border-border rounded-lg shadow-lg p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        <h2 className="font-display text-lg font-semibold text-foreground">{title}</h2>
        <div className="mt-4 space-y-3 text-sm">
          <ul className="space-y-2 text-foreground/90 list-disc pl-5">
            <li>{TRASH_RETENTION_DAYS}日後に自動で完全に消えます（それまでは元に戻せます）</li>
            <li>消えるまで容量はカウントされます</li>
            <li>共有中のリンクは、完全に消すまで開けます</li>
            {target.kind === 'project' && <li>中のファイルもまとめてゴミ箱に移します</li>}
          </ul>
          <div className="space-y-1 pt-1">
            <p className="text-muted-foreground">
              {target.kind === 'file' ? '対象ファイル' : '対象プロジェクト'}
            </p>
            <p className="font-medium break-all">{target.name}</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="px-4 py-2 border border-border rounded-md font-medium text-sm hover:bg-accent transition-colors disabled:opacity-50"
            >
              キャンセル
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isPending}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm disabled:opacity-50 hover:opacity-90 transition-opacity"
            >
              {isPending ? '移動中…' : 'ゴミ箱に移す'}
            </button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  )
}
